import { useState, useEffect } from "react";
import {
    assignVacancyToCandidate,
    changeCandidateVacancy,
    sendCandidateNotification,
    updateCandidateStatus,
    getCandidateStatus,
    sendOffer,
    updateCandidate,
    getCandidateById,
    getCandidateCompletedTests,
    getCandidateActiveVacancy,
    getCandidateVacancies,
    hireCandidate,
} from "../../services/candidateApi";
import {getVacancies} from "../../services/vacancyApi";
import { useAlertContext } from "../../context/AlertContext";
import CandidateStatusControls from "./CandidateStatusControls";
import {blockCandidate} from "../../services/blacklistApi";
import OfferModal from "./OfferModal";
import CandidateTimeline from "./CandidateTimeline";
import { relationVacancyName } from "../../utils/candidateMapper";
import { statusLabel as funnelStatusLabel, statusTone } from "../../utils/candidateStatuses";
import { buildOfferLetterText } from "../../utils/interviewInvite";
import { Chip, Section, stageChipClass } from "./CandidateProfileCard";
import HireCandidateModal from "./HireCandidateModal";
import CandidateEmploymentRequisites from "./CandidateEmploymentRequisites";
import CandidateResumeWide from "./CandidateResumeWide";

export default function CandidateDetails({ candidate, onChange, isArchiveView = false }) {
    const [vacancies, setVacancies] = useState([]);
    const [showVacancySelect, setShowVacancySelect] = useState(false);
    const [headerStatus, setHeaderStatus] = useState(candidate?.status_code ?? candidate?.status ?? "");
    const [headerStatusDate, setHeaderStatusDate] = useState(candidate?.current_status_date ?? candidate?.status_date ?? null);
    const [testResults, setTestResults] = useState([]);
    const [loadingTests, setLoadingTests] = useState(true);
    const { showAlert } = useAlertContext();
    const [offerModal, setOfferModal] = useState(false);
    const [selectedVacancyId, setSelectedVacancyId] = useState("");
    const [activeVacancy, setActiveVacancy] = useState(null);
    const [applications, setApplications] = useState([]);
    const [applicationRefresh, setApplicationRefresh] = useState(0);
    const [hireOpen, setHireOpen] = useState(false);
    const [hiring, setHiring] = useState(false);
    const [activeTab, setActiveTab] = useState("overview");
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState("");
    const [savingName, setSavingName] = useState(false);

    const saveName = async (event) => {
        event.preventDefault();
        const fullName = nameDraft.trim().replace(/\s+/g, " ");
        if (!fullName) {
            showAlert("Укажите Ф.И.О. кандидата", "warning");
            return;
        }
        if (fullName.length > 200) {
            showAlert("Ф.И.О. не должно превышать 200 символов", "warning");
            return;
        }
        try {
            setSavingName(true);
            // Name is local to HR: the HH resume and its status are not changed.
            const saved = await updateCandidate(candidate.id, { full_name: fullName });
            setEditingName(false);
            onChange?.(saved);
            showAlert("Ф.И.О. кандидата сохранено", "success");
        } catch (error) {
            showAlert(`Не удалось сохранить Ф.И.О.: ${error?.message || error}`, "error");
        } finally {
            setSavingName(false);
        }
    };

    const defaultOfferText = buildOfferLetterText(candidate.full_name);

    const submitOffer = async (offer) => {
        await sendOffer(candidate.id, offer);
        setHeaderStatus("оффер: отправлен");
        setHeaderStatusDate(null);
        showAlert("Оффер отправлен кандидату", "success");
        setOfferModal(false);
        onChange?.();
    };

    useEffect(() => {
        const fetchVacancies = async () => {
            try {
                const data = await getVacancies();
                setVacancies(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Ошибка загрузки вакансий", e);
            }
        };
        fetchVacancies();
    }, []);

    useEffect(() => {
        if (!candidate?.id) return;
        Promise.resolve(getCandidateActiveVacancy(candidate.id))
            .then((data) => setActiveVacancy(data))
            .catch(() => setActiveVacancy(null));
        Promise.resolve(getCandidateVacancies(candidate.id))
            .then((data) => setApplications(Array.isArray(data) ? data : []))
            .catch(() => setApplications([]));
    }, [candidate?.id, applicationRefresh]);

    useEffect(() => {
        const loadStatus = async () => {
            try {
                if (!candidate?.id) return;
                const statusData = await getCandidateStatus(candidate.id);
                const value = (typeof statusData === "object")
                    ? (statusData?.status ?? statusData?.code ?? statusData?.name ?? null)
                    : statusData;
                if (value != null) setHeaderStatus(value);
                if (typeof statusData === "object") setHeaderStatusDate(statusData?.status_date ?? null);
            } catch (e) {
                /* keep candidate.status */
            }
        };
        loadStatus();
    }, [candidate?.id]);

    useEffect(() => {
        if (candidate?.status != null) setHeaderStatus(candidate.status);
    }, [candidate?.status]);

    useEffect(() => {
        if (!candidate?.id) return;
        setLoadingTests(true);
        Promise.resolve(getCandidateCompletedTests(candidate.id))
            .then((data) => {
                if (Array.isArray(data)) setTestResults(data);
                else if (data?.items && Array.isArray(data.items)) setTestResults(data.items);
                else setTestResults([]);
            })
            .catch(() => setTestResults([]))
            .finally(() => setLoadingTests(false));
    }, [candidate?.id]);

    const handleBlock = async () => {
        if (!window.confirm(`Заблокировать кандидата ${candidate.full_name}? Он попадёт в чёрный список.`)) return;
        try {
            await blockCandidate(candidate.id);
            showAlert("Кандидат добавлен в чёрный список", "success");
            onChange?.();
        } catch (e) {
            showAlert(`Ошибка ${e.message}`, "error");
        }
    };

    const submitHire = async (terms) => {
        try {
            setHiring(true);
            await hireCandidate(candidate.id, terms);
            setHeaderStatus("в штате");
            setHireOpen(false);
            showAlert("Сотрудник добавлен. Дата выхода сохранена.", "success");
            onChange?.();
        } catch (error) {
            showAlert(`Не удалось добавить сотрудника: ${error?.message || error}`, "error");
        } finally {
            setHiring(false);
        }
    };

    const handleAssignVacancy = async (vacancyId) => {
        if (!vacancyId) {
            showAlert("Сначала выберите вакансию", "warning");
            return;
        }
        const vacancy = vacancies.find((v) => v.id === parseInt(vacancyId, 10));
        if (!vacancy) return;
        if (!window.confirm(`Назначить вакансию «${vacancy.name}» кандидату ${candidate.full_name}?`)) {
            setShowVacancySelect(false);
            setApplicationRefresh((value) => value + 1);
            return;
        }
        try {
            if (activeVacancy) {
                await changeCandidateVacancy(candidate.id, Number(vacancyId));
            } else {
                await assignVacancyToCandidate(candidate.id, Number(vacancyId));
            }
            try {
                await sendCandidateNotification(candidate.id, Number(vacancyId));
            } catch (e) {
                const msg = String(e?.message || e || "");
                if (msg.includes("BotUser not found")) {
                    showAlert("Вакансия назначена. У кандидата нет BotUser — уведомление не отправлено.", "warning");
                } else {
                    throw e;
                }
            }
            if (!activeVacancy) {
                await updateCandidateStatus(candidate.id, "неразобранный");
            }
            const fresh = await getCandidateById(candidate.id);
            candidate.status = fresh?.status ?? candidate.status;
            candidate.vacancy_id = fresh?.vacancy_id ?? Number(vacancyId);
            setShowVacancySelect(false);
            showAlert(
                activeVacancy
                    ? "Вакансия изменена. Кандидат возвращён на этап «Отклик получен»."
                    : "Вакансия назначена. Кандидат возвращён в активный подбор.",
                "success"
            );
            onChange?.();
        } catch (e) {
            showAlert(`Ошибка ${e.message}`, "error");
        }
    };

    const selectedApplication = applications.find((item) => Number(item.id) === Number(candidate.application_id))
        || applications.find((item) => item.is_active)
        || null;
    const isSecondaryApplication = Boolean(selectedApplication && !selectedApplication.is_active);
    const selectedStatus = selectedApplication?.status || headerStatus || candidate?.status;
    const selectedStatusDate = selectedApplication
        ? selectedApplication.status_date
        : (headerStatusDate || candidate?.current_status_date || candidate?.status_date);
    const resumeUrl = candidate.hh_resume_link || candidate.resume;
    const statusLabel = funnelStatusLabel(selectedStatus, selectedStatusDate) || selectedStatus || "Не указан";
    const vacancyTitle =
        relationVacancyName(selectedApplication)
        || relationVacancyName(activeVacancy)
        || candidate.vacancy_name
        || candidate.last_vacancy_title
        || null;
    const canChangeVacancy = candidate.stage !== "нанят" && candidate.stage !== "в черном списке";

    const tabsEl = (
            <div className="flex gap-1 rounded-2xl border border-slate-200 bg-slate-100 p-1" role="tablist" aria-label="Разделы карточки кандидата">
                {[
                    ["overview", "Резюме"],
                    ["requisites", "Реквизиты"],
                ].map(([value, label]) => (
                    <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === value}
                        onClick={() => setActiveTab(value)}
                        className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${activeTab === value ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                    >
                        {label}
                    </button>
                ))}
            </div>
    );

    return (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px] 2xl:grid-cols-[minmax(0,1fr)_460px]" data-testid="candidate-details-layout">
            <div className="min-w-0 space-y-4">
            <CandidateResumeWide
                resumeHidden={activeTab !== "overview"}
                tabs={tabsEl}
                candidate={selectedApplication ? {
                    ...candidate,
                    ai_score: selectedApplication.ai_score ?? null,
                    ai_comment: selectedApplication.ai_comment ?? null,
                } : candidate}
                vacancyTitle={vacancyTitle}
                showAi
                badges={(
                    <>
                        {candidate.stage ? (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${stageChipClass(candidate.stage)}`}>
                                {candidate.stage}
                            </span>
                        ) : null}
                        <Chip tone={{ red: "rose", amber: "amber", green: "emerald", grey: "slate" }[statusTone(selectedStatus)]}>{statusLabel}</Chip>
                        {candidate.is_not_sync ? (
                            <span
                                className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200"
                                title="Фоновая синхронизация повторит обновление автоматически"
                            >
                                Статус не синхронизирован с HH.ru
                            </span>
                        ) : null}
                    </>
                )}
                actions={(
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                setNameDraft(candidate.full_name || "");
                                setEditingName(true);
                            }}
                            className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
                        >
                            Изменить Ф.И.О.
                        </button>
                        {!isSecondaryApplication && ["Full documents", "ВНР: документы направлены"].includes(String(headerStatus || candidate?.status || "").trim()) ? (
                            <button
                                type="button"
                                onClick={() => setHireOpen(true)}
                                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                            >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                </svg>
                                Добавить в сотрудники
                            </button>
                        ) : null}
                        {!isSecondaryApplication ? <button
                            type="button"
                            onClick={() => setOfferModal(true)}
                            disabled={candidate.offer_sent === true}
                            className="px-3.5 py-2 rounded-xl bg-[#e0bb48] text-slate-900 text-sm font-semibold hover:bg-[#d4af3a] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        >
                            {candidate.offer_sent ? "Оффер отправлен" : "Направить оффер"}
                        </button> : null}
                        {resumeUrl ? (
                            <a
                                href={resumeUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
                            >
                                Открыть резюме
                            </a>
                        ) : null}
                        {canChangeVacancy ? (
                            <button
                                type="button"
                                onClick={() => setShowVacancySelect((v) => !v)}
                                className="px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
                            >
                                {vacancyTitle ? "Сменить вакансию" : "Привязать к вакансии"}
                            </button>
                        ) : null}
                        <button
                            type="button"
                            onClick={handleBlock}
                            className="px-3.5 py-2 rounded-xl border border-rose-200 bg-white text-rose-700 text-sm font-medium hover:bg-rose-50"
                        >
                            В чёрный список
                        </button>
                    </>
                )}
                belowHeader={<>
                    {editingName ? (
                        <form onSubmit={saveName} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
                            <label htmlFor="candidate-full-name" className="block text-sm font-semibold text-slate-800">Ф.И.О. кандидата на платформе</label>
                            <input
                                id="candidate-full-name"
                                value={nameDraft}
                                onChange={(event) => setNameDraft(event.target.value)}
                                maxLength={200}
                                autoFocus
                                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#cda834]/40"
                            />
                            <p className="text-xs text-slate-500">Имя изменится только на HR-платформе; профиль на HH.ru останется без изменений.</p>
                            <div className="flex gap-2">
                                <button type="submit" disabled={savingName} className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">{savingName ? "Сохраняем…" : "Сохранить"}</button>
                                <button type="button" disabled={savingName} onClick={() => setEditingName(false)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">Отмена</button>
                            </div>
                        </form>
                    ) : null}
                    {showVacancySelect && canChangeVacancy ? (
                        <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
                            <p className="text-sm text-slate-600">
                                Новая вакансия станет активной, а кандидат вернётся на этап «Отклик получен». История предыдущей вакансии сохранится.
                            </p>
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                Вакансия
                            </label>
                            <select
                                value={selectedVacancyId}
                                onChange={(e) => setSelectedVacancyId(e.target.value)}
                                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/40"
                            >
                                <option value="">Выберите вакансию</option>
                                {vacancies.map((vacancy) => (
                                    <option key={vacancy.id} value={vacancy.id}>{vacancy.name}</option>
                                ))}
                            </select>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleAssignVacancy(selectedVacancyId)}
                                    disabled={!selectedVacancyId}
                                    className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                                >
                                    Назначить
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowVacancySelect(false)}
                                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    ) : null}
                </>}
            />
                {activeTab === "requisites" ? <CandidateEmploymentRequisites candidate={candidate} /> : null}
            </div>
            <aside className="min-w-0 space-y-4 xl:sticky xl:top-0 xl:self-start" data-testid="candidate-details-sidebar">
            <Section title="Воронка и статус">
                {isSecondaryApplication ? (
                    <p className="text-sm text-slate-600">Это другой отклик кандидата. Чтобы менять его статус, сначала сделайте вакансию текущей.</p>
                ) : <CandidateStatusControls
                    candidate={{ ...candidate, status: headerStatus || candidate.status }}
                    onChange={(status, statusDate) => {
                        if (status) {
                            setHeaderStatus(status);
                            setHeaderStatusDate(statusDate || null);
                        }
                        onChange?.();
                    }}
                    onHire={() => setHireOpen(true)}
                    showOffer={false}
                />}
            </Section>

            <Section title="Отклики по вакансиям">
                {applications.length ? (
                    <div className="grid gap-2">
                        {applications.map((application) => (
                            <div key={application.id} className={`rounded-xl border px-4 py-3 ${selectedApplication?.id === application.id ? "border-amber-400 bg-amber-50/50" : "border-slate-200 bg-white"}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="font-semibold text-slate-900">{relationVacancyName(application) || `Вакансия №${application.vacancy_id}`}</div>
                                        <div className="mt-1 text-sm text-slate-600">{funnelStatusLabel(application.status, application.status_date) || application.status}</div>
                                        {application.applied_at ? <div className="mt-1 text-xs text-slate-500">Отклик: {new Date(application.applied_at).toLocaleDateString("ru-RU")}</div> : null}
                                    </div>
                                    {application.is_active ? <Chip tone="emerald">Текущая</Chip> : null}
                                </div>
                                {!application.is_active && canChangeVacancy ? (
                                    <button
                                        type="button"
                                        className="mt-3 text-sm font-semibold text-amber-800 hover:underline"
                                        onClick={() => handleAssignVacancy(application.vacancy_id)}
                                    >
                                        Сделать текущей
                                    </button>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : <p className="text-sm text-slate-500">Откликов по вакансиям пока нет.</p>}
            </Section>

            <Section title="Результаты тестов">
                {loadingTests ? (
                    <p className="text-slate-400 text-sm">Загрузка…</p>
                ) : testResults.length === 0 ? (
                    <p className="text-slate-400 text-sm">Нет результатов тестов</p>
                ) : (
                    <div className="space-y-3">
                        {testResults.map((result) => (
                            <a
                                key={`${result.source}-${result.result_id}`}
                                href={`/tests/results?candidate_id=${encodeURIComponent(candidate.id)}&type=${encodeURIComponent(result.test_type)}&source=${encodeURIComponent(result.source)}&result_id=${encodeURIComponent(result.result_id)}`}
                                className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-amber-300 hover:bg-amber-50/40 transition"
                            >
                                <span className="min-w-0">
                                    <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        {result.test_type === "psychological" ? "Психологический тест" : "Профессиональный тест"}
                                    </span>
                                    <span className="block font-medium text-slate-900 truncate">{result.title}</span>
                                    <span className="block text-xs text-slate-500 mt-1">
                                        {result.completed_at ? new Date(result.completed_at).toLocaleDateString("ru-RU") : "Дата не указана"}
                                        {result.score != null ? ` · ${result.score}${result.max_score != null ? ` / ${result.max_score}` : ""}` : ""}
                                        {result.summary ? ` · ${result.summary}` : ""}
                                    </span>
                                </span>
                                <span className="shrink-0 text-sm font-medium text-amber-700">Открыть →</span>
                            </a>
                        ))}
                    </div>
                )}
            </Section>

                <div className="xl:max-h-[70vh] xl:overflow-y-auto">
            {!isSecondaryApplication ? <CandidateTimeline
                candidateId={candidate.id}
                vacancyId={selectedApplication?.vacancy_id || activeVacancy?.id || activeVacancy?.vacancy_id}
                refreshKey={`${candidate.status || ""}-${candidate.stage || ""}-${headerStatus || ""}`}
                onChanged={onChange}
            /> : null}
                </div>
            </aside>
            <OfferModal
                open={offerModal}
                defaultText={defaultOfferText}
                defaultPosition={activeVacancy?.vacancy?.name || candidate.last_vacancy_title || ""}
                onClose={() => setOfferModal(false)}
                onSubmit={submitOffer}
            />
            <HireCandidateModal
                open={hireOpen}
                candidate={candidate}
                busy={hiring}
                onClose={() => !hiring && setHireOpen(false)}
                onSubmit={submitHire}
            />
        </div>
    );
}
